import * as Yup from "yup";

// Phone number validation helper
const phoneRegex = /^\+?[1-9]\d{1,14}$/;

// Email validation helper
const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

// Business type options
const businessTypes = [
  "Plumbing Services",
  "Electrical Services",
  "HVAC Services",
  "Landscaping & Lawn Care",
  "Cleaning Services",
  "Pest Control",
  "Roofing Services",
  "Auto Repair",
  "Dental Practice",
  "Medical Practice",
  "Legal Services",
  "Real Estate",
  "Home Renovation",
  "Photography",
  "Accounting Services",
  "Other",
];

// Time consuming task options
const timeTasks = [
  "chasing-leads",
  "admin-paperwork",
  "managing-communication",
  "quotes-estimates",
  "scheduling-coordination",
  "marketing-social",
  "inventory-procurement",
];

// Lead response options
const leadResponses = ["immediate", "few-hours", "same-day", "day-or-more"];

// Team structure options
const teamStructures = ["solo", "small-team", "sales-team", "no-owner"];

// Extra client value options
const clientValues = ["200-500", "500-1000", "1000+", "depends-on-job"];

// Missed lead cost options
const missedLeadCosts = [
  "50-100",
  "100-200",
  "200-500",
  "500-1000",
  "1000-2000",
  "2000+",
];

// Years in business options
const yearsOptions = [
  "Less than 1 year",
  "1-2 years",
  "3-5 years",
  "6-10 years",
  "11-20 years",
  "More than 20 years",
];

// Employee count options
const employeeOptions = [
  "Just me (solo)",
  "2-5 employees",
  "6-10 employees",
  "11-25 employees",
  "26-50 employees",
  "More than 50 employees",
];

// Primary goal options
const primaryGoals = [
  "increase-leads",
  "improve-followup",
  "save-time",
  "better-customer-service",
  "increase-revenue",
  "scale-business",
  "improve-efficiency",
  "competitive-advantage",
];

// Secondary goal options
const secondaryGoals = [
  "increase-leads",
  "improve-followup",
  "save-time",
  "better-customer-service",
  "increase-revenue",
  "scale-business",
  "improve-efficiency",
  "competitive-advantage",
];

// Validation schemas for each step
export const validationSchemas = {
  // Step 1: Strategic Insight
  "strategic-insight": Yup.object({
    biggestGrowthChallenge: Yup.string()
      .required("Please select your biggest growth challenge")
      .oneOf(
        [
          "getting-more-leads",
          "converting-more-leads",
          "too-many-no-shows",
          "time-management",
          "staying-organized",
          "no-systems-in-place",
          "scaling-the-team",
          "customer-retention",
        ],
        "Please select a valid option"
      ),
    customGrowthChallenge: Yup.string().notRequired(),
  }),

  // Step 2: Time Management
  "time-management": Yup.object({
    timeConsumingTasks: Yup.string()
      .required("Please select what takes most of your time")
      .oneOf(timeTasks, "Please select a valid option"),
  }),

  // Step 3: Lead Flow
  "lead-flow": Yup.object({
    leadSources: Yup.array()
      .min(1, "Please select at least one lead source")
      .of(Yup.string().required()),
    leadsPerWeek: Yup.number()
      .required("Please enter how many leads you get per week")
      .min(1, "You must get at least 1 lead per week")
      .max(1000, "Please enter a realistic number (under 1000)")
      .integer("Please enter a whole number"),
    booksAppointments: Yup.string()
      .required("Please indicate if you book appointments")
      .oneOf(["yes", "no"], "Please select yes or no"),
    noShowRate: Yup.string().notRequired(),
  }),

  // Step 4: Business Info
  "business-info": Yup.object({
    firstName: Yup.string()
      .required("First name is required")
      .trim()
      .min(2, "First name must be at least 2 characters")
      .max(50, "First name must be less than 50 characters")
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "First name can only contain letters, spaces, hyphens, and apostrophes"
      ),
    lastName: Yup.string()
      .required("Last name is required")
      .trim()
      .min(2, "Last name must be at least 2 characters")
      .max(50, "Last name must be less than 50 characters")
      .matches(
        /^[a-zA-Z\s'-]+$/,
        "Last name can only contain letters, spaces, hyphens, and apostrophes"
      ),
    email: Yup.string()
      .required("Email is required")
      .trim()
      .matches(emailRegex, "Please enter a valid email address")
      .max(254, "Email address is too long"),
    businessName: Yup.string()
      .required("Business name is required")
      .trim()
      .min(2, "Business name must be at least 2 characters")
      .max(100, "Business name must be less than 100 characters"),
    businessType: Yup.string()
      .required("Business type is required")
      .oneOf(businessTypes, "Please select a valid business type"),
    yearsInBusiness: Yup.string()
      .required("Years in business is required")
      .oneOf(yearsOptions, "Please select a valid option"),
    employeeCount: Yup.string()
      .required("Employee count is required")
      .oneOf(employeeOptions, "Please select a valid option"),
    currentRevenue: Yup.string().notRequired(),
  }),

  // Step 5: Lead Response
  "lead-response": Yup.object({
    leadResponse: Yup.string()
      .required("Please select how you respond to leads")
      .oneOf(leadResponses, "Please select a valid option"),
  }),

  // Step 6: Lost Client
  "lost-client": Yup.object({
    lostClientDueToSlowResponse: Yup.string()
      .required("Please select an option")
      .oneOf(
        ["definitely", "probably", "maybe-not-sure", "no-way"],
        "Please select a valid option"
      ),
  }),

  // Step 7: Client Value
  "client-value": Yup.object({
    extraClientValue: Yup.string()
      .required("Please select the value of an extra client")
      .oneOf(clientValues, "Please select a valid option"),
  }),

  // Step 8: Missed Lead Cost
  "missed-lead-cost": Yup.object({
    missedLeadCost: Yup.string()
      .required("Please select the cost of missed leads")
      .oneOf(missedLeadCosts, "Please select a valid option"),
  }),

  // Step 9: Team Structure
  "team-structure": Yup.object({
    teamStructure: Yup.string()
      .required("Please select your team structure")
      .oneOf(teamStructures, "Please select a valid option"),
  }),

  // Step 10: Missed Opportunities (conditional)
  "missed-opportunities-solo": Yup.object({
    missedOpportunities: Yup.string()
      .required("Please select an option")
      .oneOf(
        ["rarely", "sometimes", "often", "dont-know"],
        "Please select a valid option"
      ),
  }),

  "missed-opportunities-team": Yup.object({
    missedOpportunities: Yup.string()
      .required("Please select an option")
      .oneOf(
        ["not-really", "sometimes", "yes-costs-us", "not-sure"],
        "Please select a valid option"
      ),
  }),

  // Step 11: Lead Source Follow-up (conditional)
  "lead-source-followup": Yup.object({
    leadSourceFollowUp: Yup.string()
      .required("Please select your follow-up process")
      .oneOf(
        [
          // Website form options
          "instant-confirmation",
          "manual-followup",
          "sometimes-forget",
          "dont-know",
          // Phone call options
          "yes-system",
          "no-system",
          "sort-of",
          // DM options
          "all-manual",
          "team-helps",
          "automated-replies",
          "mostly-manual",
          "respond-when-can",
          // Legacy options (for backward compatibility)
          "basic-automation",
          "advanced-automation",
        ],
        "Please select a valid option"
      ),
  }),

  // Step 12: Automation Preferences
  "automation-preferences": Yup.object({
    automationPreferences: Yup.array()
      .min(1, "Please select at least one automation priority")
      .of(Yup.string().required()),
  }),

  // Step 13: Primary Goal
  "primary-goal": Yup.object({
    primaryGoal: Yup.string()
      .required("Please select your primary business goal")
      .oneOf(primaryGoals, "Please select a valid option"),
  }),

  // Step 14: Secondary Goals
  "secondary-goals": Yup.object({
    secondaryGoals: Yup.array()
      .min(0) // Optional step
      .of(Yup.string().oneOf(secondaryGoals)),
  }),

  // Step 15: Contact Info
  "contact-info": Yup.object({
    phone: Yup.string()
      .required("Phone number is required")
      .matches(phoneRegex, "Please enter a valid phone number")
      .min(10, "Phone number must be at least 10 digits")
      .max(15, "Phone number is too long"),
    additionalInfo: Yup.string()
      .max(1000, "Additional information must be less than 1000 characters")
      .notRequired(),
  }),
};

// Validation helper function
export const validateStep = async (stepId, formData) => {
  const schema = validationSchemas[stepId];
  if (!schema) {
    console.warn(`No validation schema found for step: ${stepId}`);
    return { isValid: true, errors: {} };
  }

  try {
    await schema.validate(formData, { abortEarly: false });
    return { isValid: true, errors: {} };
  } catch (validationError) {
    // Handle Yup validation errors
    if (validationError && validationError.inner) {
      const errors = {};
      validationError.inner.forEach((error) => {
        if (error.path) {
          errors[error.path] = error.message;
        }
      });
      return { isValid: false, errors };
    }

    // Handle other types of errors (non-Yup validation errors)
    console.warn(`Validation error for step ${stepId}:`, validationError);
    return {
      isValid: false,
      errors: {
        general:
          validationError.message ||
          "Validation failed. Please check your inputs.",
      },
    };
  }
};

// Get validation schema for a specific step
export const getValidationSchema = (stepId) => {
  return validationSchemas[stepId] || null;
};

// Check if a step has validation rules
export const hasValidationSchema = (stepId) => {
  return !!validationSchemas[stepId];
};
